import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePWA } from '@/hooks/usePWA';
import { Download, Check, Smartphone, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const InstallApp: React.FC = () => {
  const { isInstallable, installApp, isStandalone } = usePWA();
  const navigate = useNavigate();

  const handleInstall = async () => {
    await installApp();
  };

  if (isStandalone) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 text-green-600 dark:text-green-500">
              <Check className="h-6 w-6" />
              <CardTitle>App Already Installed</CardTitle>
            </div>
            <CardDescription>
              You're using the installed version of Neo Academy
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Great! You're already enjoying the full app experience with offline access and faster loading times.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2">Install Neo Academy</h1>
        <p className="text-muted-foreground">
          Get the full app experience with offline access and faster performance
        </p>
      </div>

      {/* Install Button */}
      {isInstallable && (
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Install Now
            </CardTitle>
            <CardDescription>
              Your browser supports app installation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleInstall} size="lg" className="w-full">
              <Download className="h-5 w-5 mr-2" />
              Install Neo Academy
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Benefits */}
      <Card>
        <CardHeader>
          <CardTitle>Why Install?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Offline Access</h3>
              <p className="text-sm text-muted-foreground">
                Continue learning even without internet connection
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Faster Loading</h3>
              <p className="text-sm text-muted-foreground">
                Instant access with optimized caching
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Home Screen Icon</h3>
              <p className="text-sm text-muted-foreground">
                Quick access from your device's home screen
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">App-Like Experience</h3>
              <p className="text-sm text-muted-foreground">
                Full-screen mode without browser UI
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Installation Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Install</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">iOS (iPhone/iPad)</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-7">
              <li>Tap the Share button in Safari</li>
              <li>Scroll down and tap "Add to Home Screen"</li>
              <li>Tap "Add" in the top right corner</li>
            </ol>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Android</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-7">
              <li>Tap the menu (three dots) in Chrome</li>
              <li>Tap "Install app" or "Add to Home screen"</li>
              <li>Tap "Install" in the prompt</li>
            </ol>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Monitor className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Desktop (Chrome/Edge)</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-7">
              <li>Click the install icon in the address bar</li>
              <li>Or click menu (three dots) → "Install Neo Academy"</li>
              <li>Click "Install" in the prompt</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    </div>
  );
};

export default InstallApp;
